import { createAsyncThunk } from "@reduxjs/toolkit";
import type {
  CreateUserRequest,
  CreateUserResponse,
  UpdateUserRequest,
  UpdateUserResponse,
  UsersListResponse,
} from "api-client";
import type { UserRow } from "design-components";
import { settingsUsersService } from "../../services/settings-users.service";
import type { SettingsUsersState } from "./settings-users.models";

type SettingsUsersRootSlice = { settingsUsers: SettingsUsersState };

export const fetchSettingsUsers = createAsyncThunk<
  UsersListResponse,
  void,
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/fetch", async (_, { getState, rejectWithValue }) => {
  const q = getState().settingsUsers;
  try {
    return await settingsUsersService.getUsers({
      firstName: q.firstName,
      lastName: q.lastName,
      emailId: q.emailId,
      status: q.listFilterStatus.length > 0 ? q.listFilterStatus.join(",") : undefined,
      roleName: q.listFilterRoleName.length > 0 ? q.listFilterRoleName.join(",") : undefined,
      fromIndex: q.fromIndex,
      toIndex: q.toIndex,
      sortingOrder: q.sortingOrder,
      sortingField: q.sortingField,
    });
  } catch (e: unknown) {
    const message =
      e && typeof e === "object" && "response" in e
        ? String(
            (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? "",
          )
        : "";
    return rejectWithValue(message || "Failed to fetch users");
  }
});

function rejectMessage(e: unknown, fallback: string): string {
  const message =
    e && typeof e === "object" && "response" in e
      ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message ?? "")
      : "";
  return message || fallback;
}

export const createSettingsUser = createAsyncThunk<
  CreateUserResponse,
  CreateUserRequest,
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/createUser", async (body, { dispatch, rejectWithValue }) => {
  try {
    const res = await settingsUsersService.createUser(body);
    await dispatch(fetchSettingsUsers()).unwrap();
    return res;
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to create user"));
  }
});

export const updateSettingsUser = createAsyncThunk<
  UpdateUserResponse,
  { userId: number; body: UpdateUserRequest },
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/updateUser", async ({ userId, body }, { dispatch, rejectWithValue }) => {
  try {
    const res = await settingsUsersService.updateUser(userId, body);
    await dispatch(fetchSettingsUsers()).unwrap();
    return res;
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to update user"));
  }
});

export const deleteSettingsUsers = createAsyncThunk<
  void,
  { userIds: number[] },
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/deleteUsers", async ({ userIds }, { getState, dispatch, rejectWithValue }) => {
  const rows = getState().settingsUsers.rows;
  try {
    for (const userId of userIds) {
      const row = rows.find((r) => Number(r.id) === userId);
      if (!row || row.status === "deactivated") continue;
      await settingsUsersService.updateUser(userId, {
        ...rowToUpdateBody(row),
        status: "Inactive",
      });
    }
    await dispatch(fetchSettingsUsers()).unwrap();
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to deactivate user"));
  }
});

function rowToUpdateBody(row: UserRow): UpdateUserRequest {
  return {
    firstName: row.firstName,
    lastName: row.lastName,
    roleName: row.role,
    contactNumber: row.phone ?? "",
  };
}

export const reactivateSettingsUsers = createAsyncThunk<
  void,
  number[],
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/reactivateUsers", async (userIds, { getState, dispatch, rejectWithValue }) => {
  const rows = getState().settingsUsers.rows;
  try {
    for (const userId of userIds) {
      const row = rows.find((r) => Number(r.id) === userId);
      if (!row || row.status !== "deactivated") continue;
      await settingsUsersService.updateUser(userId, {
        ...rowToUpdateBody(row),
        status: "Active",
      });
    }
    await dispatch(fetchSettingsUsers()).unwrap();
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to reactivate user"));
  }
});

export const resendInviteSettingsUsers = createAsyncThunk<
  void,
  number[],
  { state: SettingsUsersRootSlice; rejectValue: string }
>("settingsUsers/resendInvite", async (userIds, { getState, dispatch, rejectWithValue }) => {
  const rows = getState().settingsUsers.rows;
  try {
    for (const userId of userIds) {
      const row = rows.find((r) => Number(r.id) === userId);
      if (!row || row.status !== "inviteSent") continue;
      await settingsUsersService.resendInvite(userId);
    }
    await dispatch(fetchSettingsUsers()).unwrap();
  } catch (e: unknown) {
    return rejectWithValue(rejectMessage(e, "Failed to resend invitation"));
  }
});
